import React, { useState, useEffect, useRef } from "react";

// Simple, fixed-form tags inserted as-is. These cover every action the test
// caller can take in a step — including the ones that used to be a separate
// "Tool Call" action type (end the call, send DTMF, change background noise,
// wait in silence). There is no separate tool mode: everything is a tag.
const AVAILABLE_TAGS = [
  { tag: "spell", label: "Spell", example: "<spell>Sarah Connor</spell>", description: "Spell out text letter by letter" },
  { tag: "dtmf", label: "DTMF", example: '<dtmf digits="123#" />', description: "Send keypad tones" },
  { tag: "silence", label: "Silence", example: '<silence time="3s" />', description: "Pause / wait in silence" },
  { tag: "background_noise", label: "Background noise", example: '<background_noise sound="office" />', description: "Change ambient noise (use sound=\"none\" to stop)" },
  { tag: "endcall", label: "End call", example: "<endcall />", description: "Hang up the call" },
];

// Parameterized tags — the engine expects a numeric ratio (1.0 = normal).
// Inserted as <volume ratio="X" /> / <speed ratio="X" />.
const RATIO_TAGS = [
  { tag: "volume", label: "Volume", min: 0.6, max: 1.5, step: 0.1, default: 1.3, description: "Adjust speaking volume (1.0 = normal)" },
  { tag: "speed", label: "Speed", min: 0.8, max: 1.3, step: 0.1, default: 1.2, description: "Adjust speaking speed (1.0 = normal)" },
];

// Back-compat: older scenarios stored a deterministic tool as action_type
// "tool_call" + action_params.tool. The tool mode is gone, so convert such a
// step to the equivalent tag text on load — otherwise editing + re-saving an
// old scenario would silently drop its behavior.
function toolCallToTag(actionParams) {
  const tool = actionParams?.tool || "end_call";
  switch (tool) {
    case "send_dtmf":
      return `<dtmf digits="${actionParams?.digits || ""}" />`;
    case "set_background_noise":
      return `<background_noise sound="${actionParams?.noise_type || "none"}" />`;
    case "wait_for_silence":
      return '<silence time="3s" />';
    case "end_call":
    default:
      return "<endcall />";
  }
}

export default function ActionTypeSelector({ actionType, actionParams, action, fixedMessage, onChange }) {
  const [showTagHelper, setShowTagHelper] = useState(false);
  const [ratios, setRatios] = useState(() =>
    RATIO_TAGS.reduce((acc, t) => ({ ...acc, [t.tag]: t.default }), {})
  );

  // One-time migration of legacy tool_call steps → equivalent tag.
  const migratedRef = useRef(false);
  useEffect(() => {
    if (migratedRef.current) return;
    if (actionType === "tool_call") {
      migratedRef.current = true;
      onChange({
        action_type: "respond",
        action: ((action || "") + " " + toolCallToTag(actionParams)).trim(),
        action_params: {},
        fixed_message: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionType]);

  const insertTag = (tagExample) => {
    const textarea = document.getElementById("action-textarea");
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = action.slice(0, start) + tagExample + action.slice(end);
      onChange({ action: newValue });
    } else {
      onChange({ action: (action || "") + " " + tagExample });
    }
    setShowTagHelper(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-muted-foreground">
          {fixedMessage ? "Say exactly" : "Respond along these lines"}
        </label>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={fixedMessage}
              onChange={(e) => onChange({ fixed_message: e.target.checked })}
              className="rounded border-input text-primary focus:ring-primary/20"
            />
            Fixed message
          </label>
          <button
            type="button"
            onClick={() => setShowTagHelper(!showTagHelper)}
            className="text-xs text-violet-600 hover:text-violet-700 font-medium"
          >
            {showTagHelper ? "Hide tags" : "+ Insert tag"}
          </button>
        </div>
      </div>

      {showTagHelper && (
        <div className="p-2 bg-violet-50 rounded-lg border border-violet-100 space-y-2">
          {/* Fixed-form tags */}
          <div className="flex flex-wrap gap-1.5">
            {AVAILABLE_TAGS.map((t) => (
              <button
                key={t.tag}
                type="button"
                onClick={() => insertTag(t.example)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-violet-700 bg-card rounded border border-violet-200 hover:bg-violet-100 transition-colors"
                title={t.description}
              >
                {t.label}
              </button>
            ))}
          </div>
          {/* Parameterized ratio tags (volume / speed) */}
          <div className="flex flex-wrap gap-2 pt-1 border-t border-violet-100">
            {RATIO_TAGS.map((t) => (
              <div key={t.tag} className="inline-flex items-center gap-1.5 bg-card rounded border border-violet-200 px-1.5 py-1" title={t.description}>
                <span className="text-xs font-medium text-violet-700">{t.label}</span>
                <input
                  type="number"
                  min={t.min}
                  max={t.max}
                  step={t.step}
                  value={ratios[t.tag]}
                  onChange={(e) => setRatios((r) => ({ ...r, [t.tag]: e.target.value }))}
                  className="w-14 text-xs border border-violet-200 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-violet-300"
                />
                <button
                  type="button"
                  onClick={() => {
                    const raw = parseFloat(ratios[t.tag]);
                    const val = Number.isFinite(raw)
                      ? Math.min(t.max, Math.max(t.min, raw))
                      : t.default;
                    insertTag(`<${t.tag} ratio="${val}" />`);
                  }}
                  className="text-xs font-medium text-violet-700 hover:text-violet-900 px-1"
                >
                  Insert
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <textarea
        id="action-textarea"
        value={action}
        onChange={(e) => onChange({ action: e.target.value })}
        className="form-textarea text-sm font-mono"
        rows={2}
        placeholder={fixedMessage
          ? 'e.g. My name is <spell>Sarah Connor</spell> and my account is <dtmf digits="12345#" />'
          : "General guidance for the response..."}
      />
      <p className="text-xs text-muted-foreground mt-1">
        Tags: <code className="text-violet-500">&lt;spell&gt;</code>, <code className="text-violet-500">&lt;dtmf digits="…"&gt;</code>, <code className="text-violet-500">&lt;silence time="3s"&gt;</code>, <code className="text-violet-500">&lt;background_noise sound="office"&gt;</code>, <code className="text-violet-500">&lt;endcall&gt;</code>, <code className="text-violet-500">&lt;volume ratio="1.3"&gt;</code>, <code className="text-violet-500">&lt;speed ratio="1.2"&gt;</code>.
      </p>
    </div>
  );
}
